import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import HeroPanel from '../components/landing/HeroPanel';
import CoreProductClassesStrip from '../components/landing/CoreProductClassesStrip';
import MilGradeLayer from '../components/landing/MilGradeLayer';
import ProgramsPanel from '../components/landing/ProgramsPanel';
import DomainsPanel from '../components/landing/DomainsPanel';
import ConfidentialityNote from '../components/landing/ConfidentialityNote';
import ExecutionPanel from '../components/landing/ExecutionPanel';
import EvidencePackStrip from '../components/landing/EvidencePackStrip';
import ProofPointsPanel from '../components/landing/ProofPointsPanel';
import CustomersPanel from '../components/landing/CustomersPanel';
import ContactPanel from '../components/landing/ContactPanel';
import Footer from '../components/landing/Footer';
import InquiryModal from '../components/landing/InquiryModal';
import Navigation from '../components/landing/Navigation';
import { getContent, trackEvent, trackHeatmapEvent, sendVisitorHeartbeat, endSession, getSessionId } from '../services/api';

function LandingPage() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [selectedInquiryType, setSelectedInquiryType] = useState('');
  
  // Refs for section time tracking
  const sectionTimeRef = useRef({});
  const currentSectionRef = useRef(null);
  const visitedSectionsRef = useRef(new Set()); // Track visited sections for journey

  useEffect(() => {
    loadContent();
    
    // Get or create session ID (stored in sessionStorage - clears on browser close)
    const sessionId = getSessionId();
    console.log('[Session] ID:', sessionId);
    
    // Track page view with path and title
    trackEvent('page_view', { 
      path: window.location.pathname,
      url: window.location.href,
      title: document.title || 'HADEN Systems'
    }).catch(() => {});
    
    // Start visitor heartbeat (every 30 seconds)
    sendVisitorHeartbeat(window.location.pathname).catch(() => {});
    const heartbeatInterval = setInterval(() => {
      sendVisitorHeartbeat(window.location.pathname).catch(() => {});
    }, 30000);
    
    // Track scroll depth
    const handleScroll = () => {
      const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      trackHeatmapEvent('scroll', 'page', { scroll_depth: scrollDepth }).catch(() => {});
    };
    
    // Debounce scroll tracking
    let scrollTimeout;
    const debouncedScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 500);
    };
    
    window.addEventListener('scroll', debouncedScroll);
    
    // Track clicks - ensure clicks are always tracked
    const handleClick = (e) => {
      const section = e.target.closest('[data-section]');
      if (section) {
        const sectionId = section.dataset.section;
        const rect = section.getBoundingClientRect();
        const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
        const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
        trackHeatmapEvent('click', sectionId, { x, y }).catch(() => {});
      }
    };
    
    document.addEventListener('click', handleClick);
    
    // Detect if mobile device
    const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Track time on sections using Intersection Observer
    // Use lower threshold for mobile (0.2) vs desktop (0.5) for better tracking
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const sectionId = entry.target.dataset.section;
        if (!sectionId) return;
        
        if (entry.isIntersecting) {
          // Section entered viewport
          sectionTimeRef.current[sectionId] = Date.now();
          currentSectionRef.current = sectionId;
          
          // Track section visit for page journey (only once per section per session)
          if (!visitedSectionsRef.current.has(sectionId)) {
            visitedSectionsRef.current.add(sectionId);
            // Track section_view event for building page journey
            trackHeatmapEvent('section_view', sectionId, { 
              journey_order: visitedSectionsRef.current.size 
            }).catch(() => {});
          }
        } else if (sectionTimeRef.current[sectionId]) {
          // Section left viewport - track time spent
          const timeSpent = Math.round((Date.now() - sectionTimeRef.current[sectionId]) / 1000);
          if (timeSpent > 1) { // Track if more than 1 second (was 2)
            trackHeatmapEvent('time_on_section', sectionId, { time_spent: timeSpent }).catch(() => {});
          }
          delete sectionTimeRef.current[sectionId];
        }
      });
    }, { 
      threshold: isMobile ? 0.2 : 0.5,  // Lower threshold for mobile
      rootMargin: isMobile ? '0px 0px -10% 0px' : '0px'  // Trigger earlier on mobile
    });
    
    // Observe all sections
    setTimeout(() => {
      document.querySelectorAll('[data-section]').forEach(el => observer.observe(el));
    }, 1000);
    
    // Handle page visibility change (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // User switched tab or minimized - send session pause event
        trackHeatmapEvent('session_pause', 'page', { reason: 'visibility_hidden' }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Handle page unload (browser close, navigation away)
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery on page close
      endSession();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('scroll', debouncedScroll);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      observer.disconnect();
      
      // Track remaining time on current section
      if (currentSectionRef.current && sectionTimeRef.current[currentSectionRef.current]) {
        const timeSpent = Math.round((Date.now() - sectionTimeRef.current[currentSectionRef.current]) / 1000);
        if (timeSpent > 2) {
          trackHeatmapEvent('time_on_section', currentSectionRef.current, { time_spent: timeSpent }).catch(() => {});
        }
      }
    };
  }, []);

  const loadContent = async () => {
    try {
      const response = await getContent();
      setContent(response.data);
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCTAClick = (inquiryType) => {
    setSelectedInquiryType(inquiryType);
    setShowInquiryModal(true);
    
    // Track CTA click
    const eventTypeMap = {
      'Request Quote': 'cta_request_quote',
      'Technical Discussion': 'cta_technical_discussion',
      'Factory Visit': 'cta_factory_visit',
      'Upload RFQ': 'cta_upload_rfq'
    };
    trackEvent(eventTypeMap[inquiryType] || 'cta_click', { inquiry_type: inquiryType }).catch(() => {});
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Failed to load content</div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Navigation onCTAClick={handleCTAClick} />
      
      {/* 1. Hero - No spacer needed, hero extends behind transparent nav */}
      <div data-section="hero">
        <HeroPanel 
          data={content.hero} 
          onCTAClick={handleCTAClick}
        />
      </div>
      
      {/* 2. Core Product Classes (Delivered Capability) - Light section bridging Hero to MIL-Grade */}
      <div data-section="capabilities">
        <CoreProductClassesStrip />
      </div>
      
      {/* 3. MIL-Grade Engineering Layer - Dark "serious engineering" block */}
      <div data-section="mil_grade">
        <MilGradeLayer data={content.mil_grade_layer} />
      </div>
      
      {/* 4. Program Lifecycle Support */}
      <div data-section="programs">
        <ProgramsPanel data={content.programs} />
      </div>
      
      {/* 5. In-House Engineering Enablers (with Offered Variants) */}
      <div data-section="domains">
        <DomainsPanel />
      </div>
      
      {/* 6. Confidentiality Note */}
      <div data-section="confidentiality">
        <ConfidentialityNote onCTAClick={handleCTAClick} />
      </div>
      
      {/* 6. Execution Flow */}
      <div data-section="process">
        <ExecutionPanel data={content.execution_flow} />
      </div>
      
      {/* 6. Evidence Pack Strip */}
      <div data-section="certifications">
        <EvidencePackStrip data={content.evidence_pack} />
      </div>
      
      {/* 7. Proof Points / Track Record */}
      <div data-section="proof_points">
        <ProofPointsPanel data={content.proof_points} />
      </div>
      
      {/* 8. Customers (if visible) */}
      {content.customers?.visible && (
        <div data-section="customers">
          <CustomersPanel data={content.customers} />
        </div>
      )}
      
      {/* 9. Contact */}
      <div data-section="contact">
        <ContactPanel 
          data={content.contact} 
          onCTAClick={handleCTAClick}
        />
      </div>
      
      <div data-section="footer">
        <Footer data={content.contact} />
      </div>

      {showInquiryModal && (
        <InquiryModal
          isOpen={showInquiryModal}
          onClose={() => setShowInquiryModal(false)}
          defaultInquiryType={selectedInquiryType}
        />
      )}
    </div>
  );
}

export default LandingPage;
